import type {
  EventId,
  GalleryItem,
  GalleryItemId,
  GroupId,
  ImageId,
  InfoPratiquesId,
  PdfId,
} from '@code-troopers/mobile-preview/types/grab/types'
import type { DeeplinkElement as MobileDeeplinkElement } from '@code-troopers/mobile-preview/types/view/pagecontent/menu-types'
import type {
  PreviewPageParams,
  PreviewPageType,
} from '@code-troopers/mobile-preview/types/web/preview/types'

import { type GalleryItem as BackofficeGalleryItem } from '@/pages/gallery/gallery.type'

import {
  makeTranslatableText,
  type TranslatableText,
} from '../../../types/TranslatableText.type'
import { isNotNullish } from '../../utils'

export type {
  EventId,
  GalleryItem,
  GalleryItemId,
  GroupId,
  ImageId,
  InfoPratiquesId,
  PdfId,
}

// Editor-side projection of mobile's DeeplinkElement: strip the mobile base
// fields that the editor models differently (plain `id`, multilang `titles`,
// editor-only `position` / `isVisible`) and re-add them.

// Plain `Omit<Union, K>` collapses the union to the shared base; distributing
// preserves the per-variant fields (`programIds`, `url`, …).
type DistributiveOmit<T, K extends keyof any> = T extends unknown
  ? Omit<T, K>
  : never

type StripMobileBase<T> = DistributiveOmit<
  T,
  'id' | 'title' | 'icon' | 'a11yHint'
>

type EditorVariant<T> = T extends unknown
  ? StripMobileBase<T> & {
      id: string
      titles: TranslatableText
      icon: string | undefined
      position: number
      isVisible: boolean
    }
  : never

type MobileAlignedDeeplinkElement = EditorVariant<MobileDeeplinkElement>

// Variants with no mobile counterpart; the preview adapter skips them.
type BackofficeOnlyBase = {
  id: string
  titles: TranslatableText
  icon: string | undefined
  position: number
  isVisible: boolean
  privateSectionSecret?: string
  colorText?: string
  colorIcon?: string
  fontSize?: number
  fontFamily?: string
}

type BackofficeOnlyDeeplinkElement = BackofficeOnlyBase &
  ({ type: 'VIDEOS' } | { type: 'SPLASH' } | { type: 'PROGRAM_TAGS_FILTER' })

export type DeeplinkElement =
  | MobileAlignedDeeplinkElement
  | BackofficeOnlyDeeplinkElement

export type MenuElement = DeeplinkElement & { removable: boolean }

// `const` object (not an enum) so members type as their literal — matches
// the discriminator on the `DeeplinkElement` union.
export const DeeplinkElementType = {
  ABOUT: 'ABOUT',
  CASHLESS: 'CASHLESS',
  EMPTY: 'EMPTY',
  GALLERY: 'GALLERY',
  GUEST_LIST: 'GUEST_LIST',
  GUEST_LIST_TYPE: 'GUEST_LIST_TYPE',
  HOME: 'HOME',
  LIKES: 'LIKES',
  MAP: 'MAP',
  MENU: 'MENU',
  NOTIFICATIONS: 'NOTIFICATIONS',
  PRACTICAL_INFO: 'PRACTICAL_INFO',
  PRACTICAL_INFO_LIST: 'PRACTICAL_INFO_LIST',
  NEWS_LIST: 'NEWS_LIST',
  PROGRAM: 'PROGRAM',
  SAFER: 'SAFER',
  SPONSOR_LIST: 'SPONSOR_LIST',
  TICKETING: 'TICKETING',
  WEBVIEW: 'WEBVIEW',
  CHAT: 'CHAT',
  VIDEOS: 'VIDEOS',
  MY_TICKETS: 'MY_TICKETS',
  WEEZEVENT: 'WEEZEVENT',
  GUEST: 'GUEST',
  EVENT: 'EVENT',
  SPLASH: 'SPLASH',
  PROGRAM_TAGS_FILTER: 'PROGRAM_TAGS_FILTER',
} as const

export type DeeplinkElementType =
  (typeof DeeplinkElementType)[keyof typeof DeeplinkElementType]

const DEEPLINK_ELEMENT_TYPE_VALUES = new Set<string>(
  Object.values(DeeplinkElementType),
)

export function isDeeplinkElementType(v: unknown): v is DeeplinkElementType {
  return typeof v === 'string' && DEEPLINK_ELEMENT_TYPE_VALUES.has(v)
}

type ReplaceUndefined<T> = T extends undefined ? Record<string, never> : T

/**
 * Maps each DeeplinkElementType to the parameters expected by the mobile navigation stack.
 * These are derived directly from the mobile-preview package when possible.
 */
export type MobilePreviewParamsMap = {
  [K in DeeplinkElementType]: K extends PreviewPageType
    ? ReplaceUndefined<PreviewPageParams<K>>
    : K extends
          | typeof DeeplinkElementType.CASHLESS
          | typeof DeeplinkElementType.TICKETING
          | typeof DeeplinkElementType.SAFER
      ? ReplaceUndefined<PreviewPageParams<'WEBVIEW'>>
      : K extends typeof DeeplinkElementType.GUEST_LIST_TYPE
        ? ReplaceUndefined<PreviewPageParams<'GUEST_LIST'>>
        : Record<string, never>
}

export type MenuChoiceData = {
  data: string
  name: TranslatableText
  hidden: boolean
}

export type MenuChoice = MenuElement & {
  choices: MenuChoiceData[] | null
  multiselect: boolean
}

export type NavBarContent = {
  _id?: string
  type: 'BOTTOM_NAV'
  fontSize?: number
  elements: MenuElement[]
}

export type DraggableChangeType<T> = {
  added: { newIndex: number; element: T } | undefined
  removed: { oldIndex: number; element: T } | undefined
  moved: { newIndex: number; oldIndex: number; element: T } | undefined
}

type EditorBaseFields = {
  id: string
  titles: TranslatableText
  icon: string | undefined
  position: number
  isVisible: boolean
  privateSectionSecret?: string
  colorText?: string
  colorIcon?: string
  fontSize?: number
  fontFamily?: string
}

function makeEditorBase(): EditorBaseFields {
  return {
    id: '',
    titles: makeTranslatableText(),
    icon: undefined,
    position: -1,
    isVisible: true,
    fontSize: 14,
  }
}

function buildDeeplink(
  base: EditorBaseFields,
  type: DeeplinkElementType,
): DeeplinkElement {
  switch (type) {
    case DeeplinkElementType.HOME:
      return { ...base, type: 'HOME' }
    case DeeplinkElementType.MENU:
      return { ...base, type: 'MENU' }
    case DeeplinkElementType.LIKES:
      return { ...base, type: 'LIKES' }
    case DeeplinkElementType.NOTIFICATIONS:
      return { ...base, type: 'NOTIFICATIONS' }
    case DeeplinkElementType.SPONSOR_LIST:
      return { ...base, type: 'SPONSOR_LIST' }
    case DeeplinkElementType.ABOUT:
      return { ...base, type: 'ABOUT' }
    case DeeplinkElementType.EMPTY:
      return { ...base, type: 'EMPTY' }
    case DeeplinkElementType.CHAT:
      return { ...base, type: 'CHAT' }
    case DeeplinkElementType.MY_TICKETS:
      return { ...base, type: 'MY_TICKETS' }
    case DeeplinkElementType.PROGRAM:
      return {
        ...base,
        type: 'PROGRAM',
        programIds: [],
        defaultSegment: 'BY_SCENE',
      }
    case DeeplinkElementType.PRACTICAL_INFO:
      return {
        ...base,
        type: 'PRACTICAL_INFO',
        infoPratiqueId: '' as InfoPratiquesId,
      }
    case DeeplinkElementType.PRACTICAL_INFO_LIST:
      return { ...base, type: 'PRACTICAL_INFO_LIST', infoPratiqueIds: [] }
    case DeeplinkElementType.NEWS_LIST:
      return { ...base, type: 'NEWS_LIST' }
    case DeeplinkElementType.GUEST_LIST:
      return { ...base, type: 'GUEST_LIST', groupIds: [] }
    case DeeplinkElementType.GUEST_LIST_TYPE:
      return { ...base, type: 'GUEST_LIST_TYPE', typeIds: [] }
    case DeeplinkElementType.MAP:
      return { ...base, type: 'MAP', mapIds: [] }
    case DeeplinkElementType.WEBVIEW:
      return { ...base, type: 'WEBVIEW', url: '', openInWebview: true }
    case DeeplinkElementType.TICKETING:
      return { ...base, type: 'TICKETING', url: '' }
    case DeeplinkElementType.CASHLESS:
      return { ...base, type: 'CASHLESS', url: '' }
    case DeeplinkElementType.WEEZEVENT:
      return { ...base, type: 'WEEZEVENT', url: '' }
    case DeeplinkElementType.SAFER:
      return { ...base, type: 'SAFER', apiKey: '' }
    case DeeplinkElementType.GALLERY:
      return { ...base, type: 'GALLERY', galleryData: [] }
    case DeeplinkElementType.GUEST:
      return { ...base, type: 'GUEST', groupId: '' as GroupId }
    case DeeplinkElementType.EVENT:
      return { ...base, type: 'EVENT', eventId: '' as EventId }
    case DeeplinkElementType.VIDEOS:
      return { ...base, type: 'VIDEOS' }
    case DeeplinkElementType.SPLASH:
      return { ...base, type: 'SPLASH' }
    case DeeplinkElementType.PROGRAM_TAGS_FILTER:
      return { ...base, type: 'PROGRAM_TAGS_FILTER' }
  }
}

export function makeMenuElement(
  type: DeeplinkElementType = DeeplinkElementType.WEBVIEW,
): MenuElement {
  return { ...buildDeeplink(makeEditorBase(), type), removable: true }
}

// Typed read/write handle over the variant-specific data field (programIds,
// url, galleryData, …) so form code can stay variant-agnostic.
export type ElementData = string | string[] | GalleryItem[] | undefined

export function getElementData(el: DeeplinkElement): ElementData {
  switch (el.type) {
    case 'PROGRAM':
      return el.programIds
    case 'PRACTICAL_INFO':
      return el.infoPratiqueId
    case 'PRACTICAL_INFO_LIST':
      return el.infoPratiqueIds
    case 'GUEST_LIST':
      return el.groupIds
    case 'GUEST_LIST_TYPE':
      return el.typeIds
    case 'MAP':
      return el.mapIds
    case 'WEBVIEW':
    case 'TICKETING':
    case 'CASHLESS':
    case 'WEEZEVENT':
      return el.url
    case 'SAFER':
      return el.apiKey
    case 'GALLERY':
      return el.galleryData
    case 'GUEST':
      return el.groupId
    case 'EVENT':
      return el.eventId
    case 'HOME':
    case 'MENU':
    case 'LIKES':
    case 'NOTIFICATIONS':
    case 'SPONSOR_LIST':
    case 'ABOUT':
    case 'EMPTY':
    case 'CHAT':
    case 'MY_TICKETS':
    case 'VIDEOS':
    case 'SPLASH':
    case 'PROGRAM_TAGS_FILTER':
      return undefined
  }
}

// Per-variant cast to the branded mobile id types lives here so callers can
// pass raw runtime values (form input strings, choice keys) without casts.
export function setElementData<T extends DeeplinkElement>(
  el: T,
  data: ElementData,
): T {
  switch (el.type) {
    case 'PROGRAM':
      return { ...el, programIds: data as string[] } as T
    case 'PRACTICAL_INFO':
      return { ...el, infoPratiqueId: data as string } as T
    case 'PRACTICAL_INFO_LIST':
      return { ...el, infoPratiqueIds: data as string[] } as T
    case 'GUEST_LIST':
      return { ...el, groupIds: data as string[] } as T
    case 'GUEST_LIST_TYPE':
      return { ...el, typeIds: data as string[] } as T
    case 'MAP':
      return { ...el, mapIds: data as string[] } as T
    case 'WEBVIEW':
    case 'TICKETING':
    case 'CASHLESS':
    case 'WEEZEVENT':
      return { ...el, url: data as string } as T
    case 'SAFER':
      return { ...el, apiKey: data as string } as T
    case 'GALLERY':
      return {
        ...el,
        galleryData: Array.isArray(data) ? data : [],
      } as T
    case 'GUEST':
      return { ...el, groupId: data as string } as T
    case 'EVENT':
      return { ...el, eventId: data as string } as T
    default:
      return el
  }
}

// Wire shape from `/api/navbarContent` and `/api/pageContent/...`: a flat
// object with `payload: unknown` (variant-dependent — `string[]` of gallery
// ids for GALLERY, a URL for WEBVIEW, …). parse/serialize below are the
// only legitimate way to cross this boundary.
export type ServerDeeplinkElement = {
  id: string
  titles: TranslatableText
  type: DeeplinkElementType
  payload?: unknown
  icon?: string
  openInWebview?: boolean
  privateSectionSecret?: string
  defaultSegment?: 'BY_SCENE' | 'BY_HOUR'
  position?: number
  isVisible?: boolean
  removable?: boolean
  fontSize?: number
  fontFamily?: string
  colorText?: string
  colorIcon?: string
}

function parseEditorBase(s: ServerDeeplinkElement): EditorBaseFields {
  return {
    id: s.id,
    titles: s.titles,
    icon: s.icon,
    position: s.position ?? -1,
    isVisible: s.isVisible ?? true,
    privateSectionSecret: s.privateSectionSecret,
    colorText: s.colorText,
    colorIcon: s.colorIcon,
    fontSize: s.fontSize,
    fontFamily: s.fontFamily,
  }
}

// Resolve gallery ids against the editor's gallery store into mobile's
// strict `GalleryItem[]`.
export function hydrateGalleryFromIds(
  ids: string[],
  galleryRecord: Record<string, BackofficeGalleryItem>,
): GalleryItem[] {
  return ids
    .map((id) => (id in galleryRecord ? galleryRecord[id] : undefined))
    .filter(isNotNullish)
    .map((it) => ({
      weight: it.weight,
      image: it.image as PdfId,
      thumbnailImage: it.thumbnailImage as ImageId,
      title: it.titles.fr || it.titles.en || '',
      id: it._id as GalleryItemId,
    }))
}

// Shared wire→editor conversion. `parseServerMenuElement` and
// `parseServerPageElement` add role-specific fields on top.
export function parseServerDeeplinkBase(
  s: ServerDeeplinkElement,
  galleryRecord: Record<string, BackofficeGalleryItem>,
): DeeplinkElement {
  const seeded = buildDeeplink(parseEditorBase(s), s.type)
  let withData: DeeplinkElement = seeded
  if (s.payload !== undefined) {
    const data: ElementData =
      s.type === DeeplinkElementType.GALLERY && Array.isArray(s.payload)
        ? hydrateGalleryFromIds(s.payload as string[], galleryRecord)
        : (s.payload as ElementData)
    withData = setElementData(seeded, data)
  }
  if (withData.type === 'WEBVIEW' && s.openInWebview !== undefined) {
    withData = { ...withData, openInWebview: s.openInWebview }
  }
  if (withData.type === 'PROGRAM' && s.defaultSegment !== undefined) {
    withData = { ...withData, defaultSegment: s.defaultSegment }
  }
  return withData
}

export function parseServerMenuElement(
  s: ServerDeeplinkElement,
  galleryRecord: Record<string, BackofficeGalleryItem>,
): MenuElement {
  return {
    ...parseServerDeeplinkBase(s, galleryRecord),
    removable: s.removable ?? true,
  }
}

export function serializeServerDeeplinkBase(
  el: DeeplinkElement,
): ServerDeeplinkElement {
  const out: ServerDeeplinkElement = {
    id: el.id,
    titles: el.titles,
    type: el.type as DeeplinkElementType,
    icon: el.icon,
    privateSectionSecret: el.privateSectionSecret,
    position: el.position,
    isVisible: el.isVisible,
    fontSize: el.fontSize,
    fontFamily: el.fontFamily,
    colorText: el.colorText,
    colorIcon: el.colorIcon,
  }
  if (el.type === 'GALLERY') {
    out.payload = el.galleryData.map((it) => it.id)
  } else {
    const data = getElementData(el)
    if (data !== undefined) out.payload = data
  }
  if (el.type === 'WEBVIEW') out.openInWebview = el.openInWebview
  if (el.type === 'PROGRAM') out.defaultSegment = el.defaultSegment
  return out
}

export function serializeMenuElement(el: MenuElement): ServerDeeplinkElement {
  return { ...serializeServerDeeplinkBase(el), removable: el.removable }
}

export function getPreviewPageForType(
  type?: DeeplinkElementType,
): DeeplinkElementType {
  return type ?? DeeplinkElementType.HOME
}

// Params object the preview deeplink screens expect, per variant.
export function getPreviewDataForElement(
  element?: MenuElement,
):
  | MobilePreviewParamsMap[keyof MobilePreviewParamsMap]
  | Record<string, never> {
  if (!element) return {}
  switch (element.type) {
    case 'PRACTICAL_INFO':
      return { practicalInfoId: element.infoPratiqueId }
    case 'PRACTICAL_INFO_LIST':
      return { practicalInfoIds: element.infoPratiqueIds }
    case 'GUEST_LIST':
      return {
        guestIds: element.groupIds,
        guestTypeIds: undefined,
        title: element.titles.fr,
      }
    case 'GUEST_LIST_TYPE':
      return {
        guestIds: undefined,
        guestTypeIds: element.typeIds,
        title: element.titles.fr,
      }
    case 'WEBVIEW':
    case 'TICKETING':
    case 'CASHLESS':
      return {
        url: element.url,
        title: element.titles.fr,
        type: element.type,
      }
    case 'SAFER':
      return {
        url: element.apiKey,
        title: element.titles.fr,
        type: element.type,
      }
    case 'WEEZEVENT':
      return { url: element.url, title: element.titles.fr }
    case 'PROGRAM':
      return {
        programIds: element.programIds,
        title: element.titles.fr,
        defaultSegment: element.defaultSegment,
      }
    case 'GALLERY':
      return { title: element.titles.fr, items: element.galleryData }
    case 'MAP':
      return { mapIds: element.mapIds, title: element.titles.fr }
    default:
      return {}
  }
}
